import { useState } from "react";
import axios from "axios";
import { Heart, Lock, Send, CheckCircle2, Home, BookOpen, GraduationCap, Youtube, Mail } from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PrayerRequests() {
  const [form, setForm] = useState({ name: "", email: "", request: "", confidential: false, contact_me: false });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", msg: "" });
    try {
      await axios.post(`${API}/prayer-requests`, {
        name: form.name.trim(),
        email: form.email.trim() || null,
        request: form.request.trim(),
        confidential: form.confidential,
        contact_me: form.contact_me,
      });
      setStatus({ state: "success", msg: "Your prayer request has been received. We will be praying with you." });
      setForm({ name: "", email: "", request: "", confidential: false, contact_me: false });
    } catch (err) {
      setStatus({ state: "error", msg: "Something went wrong. Please try again or email us directly." });
    }
  };

  return (
    <div data-testid="prayer-page" className="bg-cream">
      {/* HERO — vertical, candle-lit feel */}
      <section className="page-hero">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(circle at 50% 30%, rgba(217,184,100,0.35) 0%, transparent 50%), linear-gradient(180deg, var(--navy-900), var(--navy-700))",
          }}
        />
        <div className="max-w-3xl mx-auto px-5 lg:px-8 py-24 lg:py-32 text-center">
          <Heart className="text-gold mx-auto" size={42} strokeWidth={1.5} />
          <h1 className="mt-6 font-display text-4xl lg:text-6xl text-cream leading-tight">
            Bring your burdens here.
          </h1>
          <p className="mt-5 text-cream/85 text-lg max-w-xl mx-auto leading-relaxed">
            No burden is too heavy for God. Share your prayer request — we will lift it before the Father with you.
          </p>
          <div className="mt-8 inline-block border-t border-b border-gold/40 py-4 px-6">
            <p className="scripture text-cream text-lg lg:text-xl">
              "The effectual fervent prayer of a righteous man availeth much."
            </p>
            <p className="text-gold-light text-xs tracking-[0.3em] mt-2 uppercase">— James 5:16</p>
          </div>
        </div>
      </section>

      {/* FORM — centered on parchment */}
      <section className="py-16 lg:py-24 -mt-16 relative z-10">
        <div className="max-w-2xl mx-auto px-5">
          <div className="bg-white rounded-3xl shadow-[0_40px_120px_-40px_rgba(11,31,58,0.35)] border border-cream-200 p-8 lg:p-12">
            {status.state === "success" ? (
              <div className="text-center py-10" data-testid="prayer-success">
                <CheckCircle2 className="text-gold mx-auto mb-5" size={56} strokeWidth={1.5} />
                <h2 className="font-display text-3xl text-navy-deep mb-3">Received in Prayer</h2>
                <p className="text-navy/70 leading-relaxed mb-7 max-w-md mx-auto">{status.msg}</p>
                <p className="scripture text-navy-deep italic">
                  "Cast thy burden upon the LORD, and he shall sustain thee." — Psalm 55:22
                </p>
                <button
                  onClick={() => setStatus({ state: "idle", msg: "" })}
                  className="mt-8 btn-outline-navy px-6 py-2.5 rounded-full"
                  data-testid="prayer-submit-another"
                >
                  Submit Another Request
                </button>
              </div>
            ) : (
              <>
                <div className="text-center mb-8">
                  <div className="text-xs uppercase tracking-[0.32em] text-gold font-medium">Prayer Request</div>
                  <h2 className="font-display text-3xl lg:text-4xl text-navy-deep mt-2">Share what's on your heart</h2>
                </div>

                <form onSubmit={submit} className="space-y-5" data-testid="prayer-form">
                  <div>
                    <label className="block text-sm font-medium text-navy mb-2">Your Name</label>
                    <input
                      data-testid="prayer-name"
                      type="text"
                      required
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-cream-200 bg-cream-50 focus-gold focus:outline-none"
                      placeholder="Jane Doe"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-navy mb-2">
                      Your Email <span className="text-navy/50 font-normal">(optional)</span>
                    </label>
                    <input
                      data-testid="prayer-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-cream-200 bg-cream-50 focus-gold focus:outline-none"
                      placeholder="you@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-navy mb-2">Prayer Request</label>
                    <textarea
                      data-testid="prayer-request"
                      required
                      rows={6}
                      value={form.request}
                      onChange={(e) => update("request", e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-cream-200 bg-cream-50 focus-gold focus:outline-none resize-y"
                      placeholder="Lord, I would like prayer for..."
                    />
                  </div>

                  <div className="space-y-3 pt-2">
                    <label className="flex items-start gap-3 cursor-pointer text-sm text-navy">
                      <input
                        data-testid="prayer-confidential"
                        type="checkbox"
                        checked={form.confidential}
                        onChange={(e) => update("confidential", e.target.checked)}
                        className="mt-1 w-4 h-4 accent-[var(--gold-500)]"
                      />
                      <span className="flex items-start gap-2">
                        <Lock size={16} className="text-gold mt-0.5" />
                        <span>Keep my request <strong>confidential</strong> — for ministry prayer only.</span>
                      </span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer text-sm text-navy">
                      <input
                        data-testid="prayer-contact-me"
                        type="checkbox"
                        checked={form.contact_me}
                        onChange={(e) => update("contact_me", e.target.checked)}
                        className="mt-1 w-4 h-4 accent-[var(--gold-500)]"
                      />
                      <span>Please contact me — I'd like to talk with someone from the ministry.</span>
                    </label>
                  </div>

                  <div className="rounded-xl p-4 bg-cream-50 border border-cream-200 flex gap-3 text-sm text-navy/75">
                    <Lock size={18} className="text-gold shrink-0 mt-0.5" />
                    <p>
                      Your request is handled with care and confidentiality by Abide Daily Ministry. We will not share it.
                      You may also email us directly at{" "}
                      <a href="mailto:abidedailyministry@gmail.com" data-testid="prayer-ministry-email"
                         className="text-gold font-semibold underline">abidedailyministry@gmail.com</a>.
                    </p>
                  </div>

                  {status.state === "error" && (
                    <p className="text-sm text-red-600" data-testid="prayer-error">{status.msg}</p>
                  )}

                  <button
                    type="submit"
                    disabled={status.state === "loading"}
                    data-testid="prayer-submit"
                    className="btn-gold w-full py-3.5 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    <Send size={16} />
                    {status.state === "loading" ? "Sending..." : "Submit Prayer Request"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Closing encouragement */}
      <section className="py-16 paper-texture text-center">
        <div className="max-w-2xl mx-auto px-5">
          <p className="scripture text-2xl lg:text-3xl text-navy-deep leading-relaxed">
            "Come unto me, all ye that labour and are heavy laden, and I will give you rest."
          </p>
          <p className="mt-3 text-gold text-xs tracking-[0.3em] uppercase">Matthew 11:28</p>
        </div>
      </section>

      {/* Bottom Nav Buttons */}
      <section className="py-16 bg-cream">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { to: "/", label: "Return Home", Icon: Home },
              { to: "/devotionals", label: "Devotionals", Icon: BookOpen },
              { to: "/bible-studies", label: "Bible Studies", Icon: GraduationCap },
              { to: "/videos", label: "Videos", Icon: Youtube },
              { to: "/contact", label: "Contact", Icon: Mail },
            ].map(({ to, label, Icon }) => (
              <Link
                key={to}
                to={to}
                data-testid={`prayer-bottom-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className="lift bg-white rounded-xl p-4 border border-cream-200 hover:border-gold flex flex-col items-center gap-2 text-center"
              >
                <Icon size={20} className="text-gold" />
                <span className="text-sm font-medium text-navy">{label}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
